import {
  COLLECTOR_ID_TYPES,
  DEATH_PLACES,
  MORTUARY_CASE_SOURCES,
  MORTUARY_CASE_STATUSES,
  MORTUARY_SLOT_STATUSES,
} from '@wodi/domain';
import { z } from 'zod';

import { unknownDescriptionSchema } from './casualty.ts';
import {
  actorSchema,
  codedRefSchema,
  codedValueSchema,
  msisdnSchema,
  reasonSchema,
} from './clinical-common.ts';
import { amountSchema, signedAmountSchema } from './money.ts';
import { birthInputSchema } from './patients.ts';
import { VITAL_REQUEST_STATUSES, vitalPersonSchema } from './vital-events.ts';

// The mortuary (docs/plans/phase2-mortuary-and-notifications.md §3). Collector details,
// notes and who brought a body are PHI: request bodies only, never a URL, never logged.

const noteSchema = z.string().trim().min(1).max(1000);

// --- The board ---------------------------------------------------------------------------------

export const SLOT_BOARD_STATES = ['occupied', 'free', 'out_of_service'] as const;

/** A body as the board shows it. `patient` is null when the reader may not see the visit. */
export const mortuaryOccupantSchema = z.object({
  caseId: z.uuid(),
  caseNumber: z.string(),
  patient: vitalPersonSchema.nullable(),
  receivedAt: z.iso.datetime(),
  /** The day of intake is day 1. */
  daysInStorage: z.number().int().min(1),
});
export type MortuaryOccupant = z.infer<typeof mortuaryOccupantSchema>;

export const mortuaryBoardSchema = z.object({
  /** False until Settings > Mortuary names the storage charge. */
  setUp: z.boolean(),
  coldRooms: z.array(
    z.object({
      name: z.string(),
      slots: z.array(
        z.object({
          id: z.uuid(),
          label: z.string(),
          state: z.enum(SLOT_BOARD_STATES),
          statusReason: z.string().nullable(),
          occupant: mortuaryOccupantSchema.nullable(),
        }),
      ),
    }),
  ),
  /** Bodies in storage not yet put in a slot. */
  unslotted: z.array(mortuaryOccupantSchema),
  /** Deaths recorded here that the mortuary has not received. */
  awaitingIntake: z.array(
    z.object({
      deathId: z.uuid(),
      patient: vitalPersonSchema,
      place: z.enum(DEATH_PLACES),
      diedAt: z.iso.datetime(),
    }),
  ),
  /** Releases asked for and waiting for a second person. */
  awaitingRelease: z.array(
    z.object({
      requestId: z.uuid(),
      caseId: z.uuid(),
      caseNumber: z.string(),
      patient: vitalPersonSchema.nullable(),
      requestedAt: z.iso.datetime(),
    }),
  ),
});
export type MortuaryBoard = z.infer<typeof mortuaryBoardSchema>;

export const mortuaryFormOptionsSchema = z.object({
  /** The tenant's sex value set, for a body nobody can name yet. */
  sexes: z.array(codedValueSchema),
});

// --- Intake --------------------------------------------------------------------------------------

const intakeCommon = {
  /** Generated once per form; a retried request returns the same case. */
  clientRequestId: z.uuid(),
  /** A free slot here, or null for "No slot yet". */
  slotId: z.uuid().nullable().default(null),
  broughtBy: noteSchema.nullable().default(null),
  intakeNotes: noteSchema.nullable().default(null),
};

/** A person brought in from outside: registered already, or nobody can name them yet. */
export const broughtInPersonSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('registered'), patientId: z.uuid() }),
  z.object({
    kind: z.literal('unknown'),
    sex: codedRefSchema.nullable().default(null),
    birth: birthInputSchema.nullable().default(null),
    description: unknownDescriptionSchema.nullable().default(null),
  }),
]);

export const receiveBodySchema = z.discriminatedUnion('source', [
  z.object({ source: z.literal('facility_death'), deathRecordId: z.uuid(), ...intakeCommon }),
  z.object({
    source: z.literal('brought_in'),
    person: broughtInPersonSchema,
    /** As reported; the death was certified elsewhere. */
    diedAt: z.iso.datetime(),
    ...intakeCommon,
  }),
]);
export type ReceiveBodyBody = z.infer<typeof receiveBodySchema>;

export const moveMortuaryBodySchema = z.object({
  slotId: z.uuid(),
  /** Needed when the body already has a slot. */
  reason: reasonSchema.optional(),
});

export const cancelMortuaryCaseSchema = z.object({ reason: reasonSchema });

// --- Release -------------------------------------------------------------------------------------

export const requestReleaseSchema = z.object({
  clientRequestId: z.uuid(),
  collectorName: z.string().trim().min(2).max(200),
  collectorRelationship: z.string().trim().min(2).max(100),
  collectorIdType: z.enum(COLLECTOR_ID_TYPES),
  collectorIdNumber: z.string().trim().min(2).max(50),
  collectorPhone: msisdnSchema.nullable().default(null),
  /** The attendant saw the collector's identity document. Must be true. */
  idChecked: z.literal(true),
  documentsNote: noteSchema.nullable().default(null),
});
export type RequestReleaseBody = z.infer<typeof requestReleaseSchema>;

export const releaseRequestSchema = z.object({
  id: z.uuid(),
  collectorName: z.string(),
  collectorRelationship: z.string(),
  collectorIdType: z.enum(COLLECTOR_ID_TYPES),
  collectorIdNumber: z.string(),
  collectorPhone: z.string().nullable(),
  idChecked: z.boolean(),
  documentsNote: z.string().nullable(),
  /** The balance owed when the release was asked for. */
  balance: amountSchema,
  status: z.enum(VITAL_REQUEST_STATUSES),
  requestedBy: actorSchema,
  requestedAt: z.iso.datetime(),
  decidedBy: actorSchema.nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
});
export type ReleaseRequestView = z.infer<typeof releaseRequestSchema>;

// --- The case ------------------------------------------------------------------------------------

export const mortuaryCaseSchema = z.object({
  id: z.uuid(),
  caseNumber: z.string(),
  patientId: z.uuid(),
  visitId: z.uuid(),
  source: z.enum(MORTUARY_CASE_SOURCES),
  status: z.enum(MORTUARY_CASE_STATUSES),
  receivedAt: z.iso.datetime(),
  receivedBy: actorSchema,
  broughtBy: z.string().nullable(),
  intakeNotes: z.string().nullable(),
  daysInStorage: z.number().int().min(1),
  death: z.object({
    id: z.uuid(),
    place: z.enum(DEATH_PLACES),
    diedAt: z.iso.datetime(),
    visitId: z.uuid(),
  }),
  slot: z.object({ id: z.uuid(), coldRoom: z.string(), label: z.string() }).nullable(),
  slotHistory: z.array(
    z.object({
      id: z.uuid(),
      coldRoom: z.string(),
      label: z.string(),
      startedAt: z.iso.datetime(),
      endedAt: z.iso.datetime().nullable(),
      endReason: z.enum(['moved', 'released', 'cancelled']).nullable(),
      reason: z.string().nullable(),
    }),
  ),
  storageDays: z.array(
    z.object({
      serviceDate: z.string(),
      /** The charge's state, or null when it was cancelled. */
      chargeStatus: z.enum(['pending', 'billed']).nullable(),
      priced: z.boolean(),
    }),
  ),
  /**
   * What is still owed: on the mortuary visit, and on the visit the person died on when
   * that was here (their stay or casualty visit).
   */
  balance: z.object({
    mortuary: signedAmountSchema,
    deathVisit: signedAmountSchema.nullable(),
    total: signedAmountSchema,
  }),
  releaseRequests: z.array(releaseRequestSchema),
  releasedAt: z.iso.datetime().nullable(),
  releaseBalance: signedAmountSchema.nullable(),
  cancelled: z.object({ reason: z.string(), at: z.iso.datetime(), by: actorSchema }).nullable(),
});
export type MortuaryCaseView = z.infer<typeof mortuaryCaseSchema>;

// --- Settings ------------------------------------------------------------------------------------

export const mortuarySlotSchema = z.object({
  id: z.uuid(),
  coldRoom: z.string(),
  label: z.string(),
  status: z.enum(MORTUARY_SLOT_STATUSES),
  statusReason: z.string().nullable(),
  sortOrder: z.number().int(),
  occupied: z.boolean(),
});
export type MortuarySlotView = z.infer<typeof mortuarySlotSchema>;

export const mortuarySettingsSchema = z.object({
  storageItem: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).nullable(),
  /** Active `mortuary` service items the storage charge can be. */
  storageItemOptions: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string() })),
  slots: z.array(mortuarySlotSchema),
});
export type MortuarySettingsView = z.infer<typeof mortuarySettingsSchema>;

export const updateMortuarySettingsSchema = z.object({ storageServiceItemId: z.uuid() });

const slotNameSchema = z.string().trim().min(1).max(60);

export const createMortuarySlotSchema = z.object({
  coldRoom: slotNameSchema,
  label: slotNameSchema,
  sortOrder: z.number().int().min(0).max(10_000).default(0),
});

export const updateMortuarySlotSchema = z
  .object({
    coldRoom: slotNameSchema.optional(),
    label: slotNameSchema.optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    status: z.enum(MORTUARY_SLOT_STATUSES).optional(),
    statusReason: reasonSchema.nullable().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Change at least one thing',
  });
